/**
 * PORTFOLIO DATA HOOK
 *
 * Manages portfolio data fetching via API endpoint.
 */

import { useState, useEffect, useCallback } from 'react';
import { ensureSessionHeaders } from '../lib/serverSession';

export interface ShareHolding {
  id: string;
  shares_purchased: number;
  total_amount: number;
  price_per_share: number;
  phase_name: string;
  phase_number: number;
  purchase_date: string;
  status: string;
  transaction_reference?: string;
}

export interface PortfolioSummary {
  totalShares: number;
  totalInvested: number;
  currentValue: number;
  unrealizedGains: number;
  unrealizedGainsPercent: number;
  averageCostPerShare: number;
  projectedAnnualDividend: number;
}

export interface KYCStatus {
  status: string;
  verification_level: string;
  documents_submitted: boolean;
  facial_recognition_completed: boolean;
  manual_review_required: boolean;
  rejection_reason?: string;
}

export interface UsePortfolioDataReturn {
  holdings: ShareHolding[];
  summary: PortfolioSummary;
  kycStatus: KYCStatus | null;
  currentPhase: any;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const usePortfolioData = (userId: number): UsePortfolioDataReturn => {
  const [holdings, setHoldings] = useState<ShareHolding[]>([]);
  const [summary, setSummary] = useState<PortfolioSummary>({
    totalShares: 0,
    totalInvested: 0,
    currentValue: 0,
    unrealizedGains: 0,
    unrealizedGainsPercent: 0,
    averageCostPerShare: 0,
    projectedAnnualDividend: 0
  });
  const [kycStatus, setKycStatus] = useState<KYCStatus | null>(null);
  const [currentPhase, setCurrentPhase] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPortfolioData = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      console.log('📊 Fetching portfolio data via API for user:', userId);

      // SECURITY v2.9.14: Send session headers for server-side userId resolution
      const response = await fetch('/api/user-portfolio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to load portfolio data');
      }

      const data = result.data;

      // Format holdings for display
      const formattedHoldings: ShareHolding[] = (data.holdings || []).map((h: any) => ({
        id: h.id,
        shares_purchased: h.shares_purchased,
        total_amount: h.total_amount,
        price_per_share: h.price_per_share || 0,
        phase_name: 'Investment Phase',
        phase_number: 0,
        purchase_date: h.purchase_date,
        status: h.status,
        transaction_reference: h.transaction_reference
      }));

      setHoldings(formattedHoldings);
      setSummary(data.summary);
      setKycStatus(data.kycStatus);
      setCurrentPhase(data.currentPhase);

      console.log('✅ Portfolio data loaded:', {
        holdings: formattedHoldings.length,
        totalShares: data.summary.totalShares
      });

    } catch (err: any) {
      console.error('Portfolio data loading error:', err);
      setError(err.message || 'Failed to load portfolio data');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadPortfolioData();
  }, [loadPortfolioData]);

  return {
    holdings,
    summary,
    kycStatus,
    currentPhase,
    loading,
    error,
    refetch: loadPortfolioData
  };
};
